import {
  type FrontendAssortmentDimensionAttribute,
  type FrontendAssortmentRowDimension,
  type FrontendAssortmentRowDimensionIdentifier,
  type GeographicDimensionAttribute,
  type ProductDimensionAttribute,
} from '../types';

type Mappers = {
  [K in FrontendAssortmentDimensionAttribute]: (
    rowDimension: Extract<FrontendAssortmentRowDimension, { name: K }>,
  ) => Extract<FrontendAssortmentRowDimensionIdentifier, { dimension: K }>;
};

const MAPPERS: Mappers = {
  all_products: () => ({
    dimension: 'all_products',
    model: null,
  }),
  all_locations: () => ({
    dimension: 'all_locations',
    model: null,
  }),
  department: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  sub_department: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  manufacturer: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  class: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  sub_class: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  style: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  color: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  material: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  gender: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  brand: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  season: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  event: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  region: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  country: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  location_type: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  location: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  collection_type: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  size: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  location_cluster: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  product_group: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  location_group: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
  product: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model.id,
  }),
  sku: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model.id,
  }),
  size_run: (rowDimension) => ({
    dimension: rowDimension.name,
    model: rowDimension.model,
  }),
};

export const mapRowDimensionToIdentifier = <
  RD extends FrontendAssortmentRowDimension,
>(
  rowDimension: RD,
): Extract<
  FrontendAssortmentRowDimensionIdentifier,
  { dimension: RD['name'] }
> => {
  type Result = Extract<
    FrontendAssortmentRowDimensionIdentifier,
    { dimension: RD['name'] }
  >;
  const mapper = MAPPERS[rowDimension.name];
  return mapper(rowDimension as never) as Result;
};

export const isMappedRowDimension = <
  T extends { name: ProductDimensionAttribute | GeographicDimensionAttribute },
>(
  rowDimension: T,
): rowDimension is Extract<T, { name: keyof Mappers }> => {
  return (
    rowDimension.name !== 'all_products' &&
    rowDimension.name !== 'all_locations'
  );
};

type SentinelDimensionIdentifier =
  | { dimension: 'all_products'; model: null }
  | { dimension: 'all_locations'; model: null };

export const isConcreteDimensionIdentifier = (
  identifier: FrontendAssortmentRowDimensionIdentifier,
): identifier is Exclude<
  FrontendAssortmentRowDimensionIdentifier,
  SentinelDimensionIdentifier
> => {
  return (
    identifier.dimension !== 'all_products' &&
    identifier.dimension !== 'all_locations'
  );
};
